import { isPaidOrder } from './orders';
// Los teléfonos llegan con y sin indicativo (573001112233, 3001112233, +57 300 111 2233):
// comparamos por los últimos 10 dígitos para reconocer a la misma persona.
export const phoneDigits = (phone) => String(phone ?? '').replace(/\D/g, '');

export const phoneKey = (phone) => phoneDigits(phone).slice(-10);

const isCompletedAppointment = (a) => ['Completada', 'completed', 'completada'].includes(a.status);
const isPaidAppointment = (a) => a.estado_pago === 'Pagado' || ['Completada', 'completed', 'completada'].includes(a.status);

// Une el historial de citas y pedidos del negocio con cada ficha de cliente.
export function buildCustomerStats(customers, appointments, orders) {
  const byKey = new Map();
  (customers || []).forEach(c => {
    byKey.set(phoneKey(c.phone), {
      customer: c, history: [], visits: 0, completedVisits: 0, orderCount: 0, totalSpent: 0, lastActivity: c.last_contact_at || null
    });
  });

  const push = (key, entry) => {
    const row = byKey.get(key);
    if (!row) return;
    row.history.push(entry);
    if (entry.date && (!row.lastActivity || String(entry.date) > String(row.lastActivity))) row.lastActivity = entry.date;
  };

  (appointments || []).forEach(a => {
    const key = phoneKey(a.phone || a.telefono || a.client_phone);
    if (!key) return;
    const amount = Number(a.precio_cobrado || a.price || 0);
    const paid = isPaidAppointment(a);
    push(key, {
      type: 'Cita',
      date: a.date || a.fecha || '',
      label: a.service || a.servicio || a.serviceName || 'Servicio',
      status: a.status || 'Pendiente',
      amount,
      paid
    });
    const row = byKey.get(key);
    if (row) {
      row.visits += 1;
      if (isCompletedAppointment(a)) row.completedVisits += 1;
      if (paid) row.totalSpent += amount;
    }
  });

  (orders || []).forEach(o => {
    const key = phoneKey(o.client_phone || o.customer_phone);
    if (!key) return;
    const amount = Number(o.total ?? o.total_price ?? 0);
    const paid = isPaidOrder(o);
    push(key, {
      type: 'Pedido',
      date: (o.created_at || '').split('T')[0],
      label: `Pedido #${String(o.id || '').slice(0, 5)}`,
      status: o.status || 'pending',
      amount,
      paid
    });
    const row = byKey.get(key);
    if (row) {
      row.orderCount += 1;
      if (paid) row.totalSpent += amount;
    }
  });

  byKey.forEach(row => row.history.sort((x, y) => String(y.date).localeCompare(String(x.date))));
  return byKey;
}

const DAY_MS = 24 * 60 * 60 * 1000;
export const daysSince = (iso, now = new Date()) => {
  if (!iso) return null;
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? null : Math.floor((now - t) / DAY_MS);
};

// Días sin actividad (mensaje, cita o pedido) para pasar solo a "inactivo".
export const INACTIVE_DAYS = 60;

// Embudo de ventas. Automáticas (se calculan solas): lead, cliente, inactivo.
// Manuales: contactado, interesado, negociacion (solo mientras sea lead), vip y perdido.
export const PROSPECT_STAGES = ['lead', 'contactado', 'interesado', 'negociacion'];
const PIPELINE_STATUSES = ['contactado', 'interesado', 'negociacion'];

// lead = escribió pero nunca agendó/pidió; cliente = ya tiene al menos una cita o pedido;
// inactivo = sin actividad hace más de INACTIVE_DAYS (o marcado a mano). VIP y perdido siempre son manuales.
// Las etapas de seguimiento las pone una persona y mandan mientras no sea cliente: apenas agenda o pide,
// pasa sola a cliente.
export function customerStage(customer, stats, now = new Date()) {
  const status = customer.status;
  if (status === 'vip' || status === 'inactivo' || status === 'perdido') return status;
  const isClient = (stats?.visits || 0) + (stats?.orderCount || 0) > 0;
  if (PIPELINE_STATUSES.includes(status) && !isClient) return status;
  const days = daysSince(stats?.lastActivity ?? customer.last_contact_at, now);
  if (days !== null && days >= INACTIVE_DAYS) return 'inactivo';
  return isClient ? 'cliente' : 'lead';
}

// Seguimiento: fecha (sin hora) en la que hay que volver a contactar a la persona.
const pad2 = (n) => String(n).padStart(2, '0');
export const localDateStr = (d = new Date()) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export const addDaysStr = (n, now = new Date()) => {
  const d = new Date(now);
  d.setDate(d.getDate() + n);
  return localDateStr(d);
};
export function followUpState(customer, now = new Date()) {
  const due = customer?.next_follow_up_at;
  if (!due) return null;
  const day = String(due).slice(0, 10);
  const today = localDateStr(now);
  if (day < today) return 'vencido';
  return day === today ? 'hoy' : 'proximo';
}
export const isFollowUpDue = (customer, now = new Date()) => ['vencido', 'hoy'].includes(followUpState(customer, now));

// WhatsApp a veces no entrega el número sino un ID interno (@lid). n8n lo guarda con la etiqueta 'lid'.
export const isLidOnly = (customer) => Array.isArray(customer?.tags) && customer.tags.includes('lid');

// Al silenciar un número: si su ficha ya tiene ID interno el bot lo reconoce solo; si no, su chat puede estar entrando como un contacto «sin número»
// y hay que decir cuál es. Devuelve esos contactos sin vincular, el más reciente primero (máximo `limit`).
export const lidSuggestions = (customers, phone, limit = 5) => {
  const key = phoneKey(phone);
  const rows = Array.isArray(customers) ? customers : [];
  if (key.length < 7) return [];
  if (rows.some((c) => !isLidOnly(c) && phoneKey(c.phone) === key && c.lid)) return [];
  return rows
    .filter(isLidOnly)
    .sort((a, b) => String(b.last_contact_at || '').localeCompare(String(a.last_contact_at || '')))
    .slice(0, limit);
};

export const phoneLabel = (customer) => (isLidOnly(customer) ? 'Sin número visible' : `+${customer.phone}`);

const SOURCE_LABELS = { whatsapp: 'WhatsApp', cita: 'Citas', pedido: 'Pedidos', manual: 'Manual' };

// Audiencia de campañas: toda ficha con número real. Los contactos sin número (@lid) no se pueden incluir
// hasta que se les vincule un teléfono; se cuentan aparte para avisarlo.
export function buildAudience(customers, statsByKey, now = new Date()) {
  const audience = [];
  let withoutPhone = 0;
  let notInterested = 0;
  (customers || []).forEach(c => {
    if (c.status === 'perdido') { notInterested += 1; return; } // pidió que no se le escriba o no le interesa
    if (isLidOnly(c)) { withoutPhone += 1; return; }
    const stats = statsByKey?.get(phoneKey(c.phone));
    const lastActivity = stats?.lastActivity || c.last_contact_at || null;
    audience.push({
      id: c.id,
      name: c.name || 'Cliente',
      phone: phoneDigits(c.phone),
      totalSpent: stats?.totalSpent || 0,
      appointmentsCount: (stats?.visits || 0) + (stats?.orderCount || 0),
      lastActivity,
      lastVisit: lastActivity ? String(lastActivity).split('T')[0] : '—',
      source: SOURCE_LABELS[c.source] || 'Manual',
      stage: customerStage(c, stats, now)
    });
  });
  return { audience, withoutPhone, notInterested };
}

// Segmentos de campaña: salen de la etapa de cada cliente, no de listas que haya que armar a mano.
export function inSegment(item, segment, now = new Date()) {
  const days = daysSince(item.lastActivity, now);
  switch (segment) {
    case 'lead': return PROSPECT_STAGES.includes(item.stage);
    case 'cliente': return item.stage === 'cliente';
    case 'vip': return item.stage === 'vip' || item.appointmentsCount >= 3 || item.totalSpent >= 50000;
    case 'inactive': return item.stage === 'inactivo' || (days !== null && days >= 30);
    case 'recent': return days !== null && days <= 7;
    default: return true;
  }
}

// Tarjeta de sellos por cliente: cada `required` citas completadas gana una recompensa.
export function loyaltyProgress(completedVisits, redeemed, required) {
  const req = Math.max(1, Number(required) || 5);
  const done = Math.max(0, Number(completedVisits) || 0);
  const used = Math.max(0, Number(redeemed) || 0);
  const earned = Math.floor(done / req);
  return { required: req, stamps: done % req, earned, redeemed: used, available: Math.max(0, earned - used) };
}

// Resumen del CRM para el Dashboard.
export function crmSummary(customers, statsByKey, now = new Date()) {
  const out = { total: 0, newThisWeek: 0, leads: 0, clientes: 0, vip: 0, inactivos: 0, perdidos: 0, dueFollowUps: 0, withoutPhone: 0, converted: 0, conversionPct: 0 };
  (customers || []).forEach(c => {
    const stats = statsByKey?.get(phoneKey(c.phone));
    const stage = customerStage(c, stats, now);
    out.total += 1;
    if (PROSPECT_STAGES.includes(stage)) out.leads += 1;
    else if (stage === 'cliente') out.clientes += 1;
    else if (stage === 'vip') out.vip += 1;
    else if (stage === 'perdido') out.perdidos += 1;
    else out.inactivos += 1;
    const firstDays = daysSince(c.first_contact_at, now);
    if (firstDays !== null && firstDays <= 7) out.newThisWeek += 1;
    if (isFollowUpDue(c, now)) out.dueFollowUps += 1;
    if (isLidOnly(c)) out.withoutPhone += 1;
    if ((stats?.visits || 0) + (stats?.orderCount || 0) > 0) out.converted += 1;
  });
  out.conversionPct = out.total ? Math.round((out.converted * 100) / out.total) : 0;
  return out;
}

// ---- CSV ----
const STAGE_LABEL = { lead: 'Lead', contactado: 'Contactado', interesado: 'Interesado', negociacion: 'En negociación', cliente: 'Cliente', vip: 'VIP', perdido: 'Perdido', inactivo: 'Inactivo' };
const CSV_HEADERS = ['Nombre', 'Teléfono', 'Correo', 'Etapa', 'Citas', 'Pedidos', 'Gastado', 'Último contacto', 'Próximo seguimiento', 'Etiquetas', 'Notas', 'Lista de precios'];

// Una celda que empiece con = + - @ podría ejecutarse como fórmula al abrirla en Excel: se neutraliza.
const csvCell = (value) => {
  let text = String(value ?? '');
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[";,\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// `rows` son las filas de la pantalla: { customer, stage, visits, orderCount, totalSpent, lastActivity }.
// Separador ";" y BOM para que Excel en español lo abra con tildes y en columnas.
export function customersToCsv(rows) {
  const lines = [CSV_HEADERS.join(';')];
  (rows || []).forEach(r => {
    const c = r.customer;
    lines.push([
      c.name, isLidOnly(c) ? '' : c.phone, c.email, STAGE_LABEL[r.stage] || '',
      r.visits || 0, r.orderCount || 0, r.totalSpent || 0,
      String(r.lastActivity || c.last_contact_at || '').split('T')[0],
      String(c.next_follow_up_at || '').slice(0, 10),
      (c.tags || []).filter(t => t !== 'lid').join(' | '), c.notes, c.price_list_key
    ].map(csvCell).join(';'));
  });
  return `\uFEFF${lines.join('\r\n')}`;
}

function splitCsvLine(line, delimiter) {
  const cells = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') { cell += '"'; i += 1; }
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delimiter) { cells.push(cell); cell = ''; }
    else cell += ch;
  }
  cells.push(cell);
  return cells;
}

// Acepta ";" "," o tabulador, con o sin fila de títulos (Nombre, Teléfono, Correo, Lista de precios).
// Sin títulos: nombre, teléfono, correo en ese orden, o solo el teléfono.
export function parseCustomersCsv(text, options = {}) {
  const lines = String(text || '').replace(/^\uFEFF/, '').split(/\r?\n/).filter(l => l.trim());
  if (lines.length === 0) return { rows: [], skipped: 0, unknownLists: [] };
  const delimiter = [';', '\t', ','].find(d => lines[0].includes(d)) || ',';
  return parseCustomerCells(lines.map(line => splitCsvLine(line, delimiter)), options);
}

const normText = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Mismo análisis para filas ya separadas en celdas (CSV o Excel). `options.lists` = [{ key, name }]: la columna «Lista de precios» (o Tarifa,
// Tipo de cliente) puede traer la clave o el nombre de la lista; las que no existan se devuelven en `unknownLists` y el cliente se importa sin lista.
export function parseCustomerCells(cellRows, options = {}) {
  const all = (cellRows || []).filter(r => Array.isArray(r) && r.some(c => String(c ?? '').trim() !== ''));
  if (all.length === 0) return { rows: [], skipped: 0, unknownLists: [] };
  const first = all[0].map(h => String(h ?? '').trim().toLowerCase());
  const hasHeader = first.some(h => /nombre|name|tel|cel|phone|whatsapp|correo|email|mail/.test(h));
  let nameIdx = 0;
  let phoneIdx = 1;
  let emailIdx = 2;
  let listIdx = -1;
  let body = all;
  if (hasHeader) {
    nameIdx = first.findIndex(h => /nombre|name/.test(h));
    phoneIdx = first.findIndex(h => /tel|cel|phone|whatsapp/.test(h));
    emailIdx = first.findIndex(h => /correo|email|mail/.test(h));
    listIdx = first.findIndex(h => /lista|tarifa|tipo de cliente|tipo cliente|price/.test(h));
    body = all.slice(1);
  }
  const byText = new Map();
  (options.lists || []).forEach(l => { byText.set(normText(l.key), l.key); byText.set(normText(l.name), l.key); });
  const unknown = new Set();
  const rows = [];
  let skipped = 0;
  body.forEach(cells => {
    const onlyPhone = !hasHeader && cells.length === 1;
    const phone = phoneDigits(String(onlyPhone ? cells[0] : cells[phoneIdx] ?? ''));
    if (phone.length < 7) { skipped += 1; return; }
    const row = {
      name: onlyPhone || nameIdx < 0 ? '' : String(cells[nameIdx] ?? '').trim(),
      phone,
      email: onlyPhone || emailIdx < 0 ? '' : String(cells[emailIdx] ?? '').trim()
    };
    const rawList = listIdx < 0 ? '' : String(cells[listIdx] ?? '').trim();
    if (rawList) {
      const key = byText.get(normText(rawList));
      if (key) row.price_list_key = key;
      else unknown.add(rawList);
    }
    rows.push(row);
  });
  return { rows, skipped, unknownLists: [...unknown] };
}
